import {FC, ReactNode, useEffect, useState} from 'react'
import {api, PaymentTransaction, PaymentStats} from '../../services/api'
import {usePaginated} from '../../hooks/usePaginated'
import {Pagination} from '../../components/Pagination'
import {DisputesPanel} from '../disputes/DisputesPage'

const CARD = {
  background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
}

const moneyIcon = (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <line x1='12' y1='1' x2='12' y2='23' /><path d='M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6' />
  </svg>
)
const txIcon = (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <polyline points='17 1 21 5 17 9' /><path d='M3 11V9a4 4 0 0 1 4-4h14' />
    <polyline points='7 23 3 19 7 15' /><path d='M21 13v2a4 4 0 0 1-4 4H3' />
  </svg>
)
const disputeIcon = (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <path d='M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' />
    <line x1='12' y1='9' x2='12' y2='13' /><line x1='12' y1='17' x2='12.01' y2='17' />
  </svg>
)
const failedIcon = (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <circle cx='12' cy='12' r='10' /><line x1='15' y1='9' x2='9' y2='15' /><line x1='9' y1='9' x2='15' y2='15' />
  </svg>
)

const TX_STATUS: Record<string, {bg: string; color: string}> = {
  completed: {bg: '#dcfce7', color: '#16a34a'},
  pending:   {bg: '#fef9c3', color: '#a16207'},
  refunded:  {bg: '#dbeafe', color: '#2563eb'},
  failed:    {bg: '#fee2e2', color: '#dc2626'},
}

const humanizeMethod = (m: string): string => {
  if (m === 'card') return 'Credit Card'
  if (m === 'paypal') return 'PayPal'
  return m ? m.charAt(0).toUpperCase() + m.slice(1) : '—'
}

const fmtDateTime = (iso: string): string => {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// ── KPI card ──────────────────────────────────────────────────────────────────
const KpiCard: FC<{label: string; value: ReactNode; icon: ReactNode; bg: string; iconColor: string; valueColor?: string}> =
  ({label, value, icon, bg, iconColor, valueColor}) => (
    <div style={{...CARD, padding: '20px 20px'}}>
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16}}>
        <div style={{width: 44, height: 44, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: iconColor}}>
          {icon}
        </div>
      </div>
      <div style={{fontSize: 26, fontWeight: 700, color: valueColor ?? '#0f172a', marginBottom: 4}}>{value}</div>
      <div style={{fontSize: 13, color: '#94a3b8'}}>{label}</div>
    </div>
  )

// ── Transactions tab ──────────────────────────────────────────────────────────
const TransactionsCard: FC = () => {
  const {items, total, page, setPage, search, setSearch, limit, loading, error} =
    usePaginated<PaymentTransaction>((p, l, s) => api.getPaymentTransactions(p, l, s), {limit: 20})

  const inputStyle = {
    width: '100%', padding: '10px 14px 10px 38px', border: '1px solid #e2e8f0',
    borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#fff',
    outline: 'none', boxSizing: 'border-box' as const,
  }

  return (
    <div style={{...CARD, overflow: 'hidden'}}>
      {/* Search */}
      <div style={{padding: '16px 20px', borderBottom: '1px solid #f1f5f9'}}>
        <div style={{position: 'relative', maxWidth: 360}}>
          <svg style={{position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8'}} width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <circle cx='11' cy='11' r='8' /><line x1='21' y1='21' x2='16.65' y2='16.65' />
          </svg>
          <input type='text' placeholder='Search transactions…' value={search}
            onChange={e => setSearch(e.target.value)} style={inputStyle} />
        </div>
      </div>

      {error && (
        <div style={{padding: '12px 20px', background: '#fef2f2', color: '#dc2626', fontSize: 14, borderBottom: '1px solid #fecaca'}}>{error}</div>
      )}

      {loading ? (
        <div style={{display: 'flex', justifyContent: 'center', padding: '60px 0'}}>
          <div style={{width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />
        </div>
      ) : (
        <div style={{overflowX: 'auto'}}>
          <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
            <thead>
              <tr style={{background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                {['TRANSACTION ID', 'BOOKING', 'USER', 'AMOUNT', 'SERVICE FEE', 'METHOD', 'DATE', 'STATUS'].map(h => (
                  <th key={h} style={{padding: '11px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((t, i) => {
                const st = TX_STATUS[t.status] ?? {bg: '#f1f5f9', color: '#64748b'}
                return (
                  <tr key={t.id} style={{borderBottom: i < items.length - 1 ? '1px solid #f1f5f9' : 'none'}}
                    onMouseEnter={ev => (ev.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}>
                    <td style={{padding: '14px 20px', fontFamily: 'monospace', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap'}}>{t.transactionRef}</td>
                    <td style={{padding: '14px 20px', fontFamily: 'monospace', color: '#64748b', whiteSpace: 'nowrap'}}>{t.bookingRef || '—'}</td>
                    <td style={{padding: '14px 20px', minWidth: 160}}>
                      <div style={{fontWeight: 600, color: '#0f172a'}}>{t.userName || '—'}</div>
                      {t.userEmail && <div style={{fontSize: 12, color: '#94a3b8'}}>{t.userEmail}</div>}
                    </td>
                    <td style={{padding: '14px 20px', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap'}}>${Number(t.amount).toLocaleString()}</td>
                    <td style={{padding: '14px 20px', fontWeight: 600, color: '#16a34a', whiteSpace: 'nowrap'}}>${Number(t.serviceFee || 0).toFixed(2)}</td>
                    <td style={{padding: '14px 20px', color: '#374151', whiteSpace: 'nowrap'}}>{humanizeMethod(t.method)}</td>
                    <td style={{padding: '14px 20px', color: '#64748b', whiteSpace: 'nowrap'}}>{fmtDateTime(t.createdAt)}</td>
                    <td style={{padding: '14px 20px'}}>
                      <span style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: st.bg, color: st.color, textTransform: 'capitalize'}}>{t.status}</span>
                    </td>
                  </tr>
                )
              })}
              {items.length === 0 && (
                <tr><td colSpan={8} style={{padding: '48px 20px', textAlign: 'center', color: '#94a3b8'}}>No transactions</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > 0 && (
        <div style={{padding: '12px 20px', borderTop: '1px solid #f1f5f9'}}>
          <Pagination page={page} limit={limit} total={total} onPageChange={setPage} />
        </div>
      )}
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────
const PaymentsDisputesPage: FC = () => {
  const [stats, setStats] = useState<PaymentStats | null>(null)
  const [pendingDisputes, setPendingDisputes] = useState<number | null>(null)
  const [tab, setTab] = useState<'transactions' | 'disputes'>('transactions')

  useEffect(() => {
    let alive = true
    Promise.all([
      api.getPaymentStats(),
      api.getDisputesPaged(1, 1, '', {status: 'pending'}),
    ])
      .then(([s, d]) => {
        if (!alive) return
        setStats(s)
        setPendingDisputes(d.total)
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [])

  const dash = '—'

  const tabBtn = (key: 'transactions' | 'disputes', label: string, badge?: number): ReactNode => {
    const active = tab === key
    return (
      <button onClick={() => setTab(key)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '12px 4px', marginRight: 28,
          background: 'none', border: 'none', borderBottom: `2px solid ${active ? '#3b82f6' : 'transparent'}`,
          fontSize: 14, fontWeight: 600, color: active ? '#3b82f6' : '#64748b', cursor: 'pointer',
        }}>
        {label}
        {badge != null && badge > 0 && (
          <span style={{minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, background: '#dc2626', color: '#fff', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center'}}>
            {badge}
          </span>
        )}
      </button>
    )
  }

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom: 24}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Payments &amp; Disputes</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>Manage transactions and resolve disputes</p>
      </div>

      {/* KPI cards */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 24}}>
        <KpiCard label='Total Revenue' bg='#f0fdf4' iconColor='#16a34a' icon={moneyIcon}
          value={stats ? `$${stats.totalRevenue.toLocaleString()}` : dash} />
        <KpiCard label='Total Transactions' bg='#eff6ff' iconColor='#3b82f6' icon={txIcon}
          value={stats ? stats.totalTransactions.toLocaleString() : dash} />
        <KpiCard label='Pending Disputes' bg='#fefce8' iconColor='#ca8a04' icon={disputeIcon} valueColor='#ca8a04'
          value={pendingDisputes != null ? pendingDisputes.toLocaleString() : dash} />
        <KpiCard label='Failed Payments' bg='#fef2f2' iconColor='#dc2626' icon={failedIcon} valueColor='#dc2626'
          value={stats ? stats.failedPayments.toLocaleString() : dash} />
      </div>

      {/* Tab bar */}
      <div style={{display: 'flex', alignItems: 'center', borderBottom: '1px solid #e2e8f0', marginBottom: 20}}>
        {tabBtn('transactions', 'Transaction History')}
        {tabBtn('disputes', 'Disputes', pendingDisputes ?? 0)}
      </div>

      {/* Tab content */}
      {tab === 'transactions' ? <TransactionsCard /> : <DisputesPanel showHeader={false} />}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

const PaymentsDisputesPageWrapper: FC = () => <PaymentsDisputesPage />

export {PaymentsDisputesPageWrapper}
